/**
 * API Service
 * 
 * This module contains all API-related functions for interacting with backend services.
 * It abstracts away the implementation details of API calls from the components.
 */

import { Enterprise } from '../types/enterprise';
import { Video, VideoComment } from '../types/video';
import { Insight } from '../types/insight';
import { API_CONFIG } from '../config/env';

/**
 * Generic error handler for API requests
 */
const handleApiError = (error: any): never => {
  console.error('API Error:', error);
  throw new Error(error.message || 'An unknown error occurred');
};

/**
 * Fetch all enterprises from the API
 */
export const fetchEnterprises = async (): Promise<Enterprise[]> => {
  try {
    // In a real app, this would be a real API call
    // const response = await fetch(`${API_CONFIG.BASE_URL}/api/${API_CONFIG.VERSION}/enterprises`);
    // if (!response.ok) throw new Error('Failed to fetch enterprises');
    // return await response.json();
    
    // For now, return mock data
    return mockEnterprises;
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Follow an enterprise
 */
export const followEnterprise = async (enterpriseId: string, userId: string): Promise<void> => {
  try {
    // In a real app, this would be a real API call
    // const response = await fetch(`${API_CONFIG.BASE_URL}/api/${API_CONFIG.VERSION}/users/${userId}/follow/${enterpriseId}`, {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //   },
    // });
    // if (!response.ok) throw new Error('Failed to follow enterprise');
    
    // For now, just log it
    console.log(`User ${userId} followed enterprise ${enterpriseId}`);
  } catch (error) {
    handleApiError(error);
  }
};

/**
 * Unfollow an enterprise
 */
export const unfollowEnterprise = async (enterpriseId: string, userId: string): Promise<void> => {
  try {
    // In a real app, this would be a real API call
    // const response = await fetch(`${API_CONFIG.BASE_URL}/api/${API_CONFIG.VERSION}/users/${userId}/unfollow/${enterpriseId}`, {
    //   method: 'DELETE',
    // });
    // if (!response.ok) throw new Error('Failed to unfollow enterprise');
    
    // For now, just log it
    console.log(`User ${userId} unfollowed enterprise ${enterpriseId}`);
  } catch (error) {
    handleApiError(error);
  }
};

/**
 * Submit a video to an enterprise
 */
export const submitVideo = async (enterpriseId: string, videoData: any): Promise<void> => {
  try {
    // In a real app, this would be a real API call
    // const response = await fetch(`${API_CONFIG.BASE_URL}/api/${API_CONFIG.VERSION}/enterprises/${enterpriseId}/videos`, {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //   },
    //   body: JSON.stringify(videoData),
    // });
    // if (!response.ok) throw new Error('Failed to submit video');
    
    // For now, just log it
    console.log(`Video submitted to enterprise ${enterpriseId}`, videoData);
  } catch (error) {
    handleApiError(error);
  }
};

/**
 * Get enterprise details
 */
export const getEnterpriseDetails = async (enterpriseId: string): Promise<Enterprise> => {
  try {
    // In a real app, this would be a real API call
    // const response = await fetch(`${API_CONFIG.BASE_URL}/api/${API_CONFIG.VERSION}/enterprises/${enterpriseId}`);
    // if (!response.ok) throw new Error('Failed to fetch enterprise details');
    // return await response.json();
    
    // For now, return mock data
    const enterprise = mockEnterprises.find(e => e.id === enterpriseId);
    if (!enterprise) {
      throw new Error(`Enterprise with ID ${enterpriseId} not found`);
    }
    return enterprise;
  } catch (error) {
    return handleApiError(error);
  }
};

// Mock data for development
const mockEnterprises: Enterprise[] = [
  {
    id: 'kayak',
    name: 'Kayak',
    category: 'Travel',
    logo: '/images/kayak-icon.svg',
    topEarnings: 300000,
    creators: 1200,
    active: 451
  },
  {
    id: 'mindmatrix',
    name: 'Mindmatrix',
    category: 'B2B',
    logo: '/images/mindmatrix-icon.svg',
    topEarnings: 21000,
    creators: 45,
    active: 23
  },
  {
    id: 'emirates',
    name: 'Emirates',
    category: 'Luxury',
    logo: '/images/emirates-icon.svg',
    topEarnings: 10000,
    creators: 1200,
    active: 451
  }
];

/**
 * Fetch trending videos
 */
export const fetchTrendingVideos = async (): Promise<Video[]> => {
  try {
    // In a real app, this would be a real API call
    // const response = await fetch(`${API_CONFIG.BASE_URL}/api/${API_CONFIG.VERSION}/videos/trending`);
    // if (!response.ok) throw new Error('Failed to fetch trending videos');
    // return await response.json();
    
    // For now, return mock data
    return mockTrendingVideos;
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Like a video
 */
export const likeVideo = async (videoId: string, userId: string): Promise<void> => {
  try {
    // In a real app, this would be a real API call
    // const response = await fetch(`${API_CONFIG.BASE_URL}/api/${API_CONFIG.VERSION}/videos/${videoId}/like`, {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //   },
    //   body: JSON.stringify({ userId }),
    // });
    // if (!response.ok) throw new Error('Failed to like video');
    
    // For now, just log it
    console.log(`User ${userId} liked video ${videoId}`);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Unlike a video
 */
export const unlikeVideo = async (videoId: string, userId: string): Promise<void> => {
  try {
    // In a real app, this would be a real API call
    // const response = await fetch(`${API_CONFIG.BASE_URL}/api/${API_CONFIG.VERSION}/videos/${videoId}/unlike`, {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //   },
    //   body: JSON.stringify({ userId }),
    // });
    // if (!response.ok) throw new Error('Failed to unlike video');
    
    // For now, just log it
    console.log(`User ${userId} unliked video ${videoId}`);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Fetch comments for a video
 */
export const fetchVideoComments = async (videoId: string): Promise<VideoComment[]> => {
  try {
    // In a real app, this would be a real API call
    // const response = await fetch(`${API_CONFIG.BASE_URL}/api/${API_CONFIG.VERSION}/videos/${videoId}/comments`);
    // if (!response.ok) throw new Error('Failed to fetch video comments');
    // return await response.json();
    
    // For now, return mock data
    return mockComments.filter(comment => comment.videoId === videoId);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Add a comment to a video
 */
export const addVideoComment = async (
  videoId: string,
  userId: string,
  userName: string,
  userAvatar: string,
  content: string
): Promise<VideoComment> => {
  try {
    // In a real app, this would be a real API call
    // const response = await fetch(`${API_CONFIG.BASE_URL}/api/${API_CONFIG.VERSION}/videos/${videoId}/comments`, {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //   },
    //   body: JSON.stringify({ userId, userName, userAvatar, content }),
    // });
    // if (!response.ok) throw new Error('Failed to add comment');
    // return await response.json();
    
    // For now, create a mock comment
    const newComment: VideoComment = {
      id: `comment-${Date.now()}`,
      videoId,
      userId,
      userName,
      userAvatar,
      content,
      createdAt: new Date().toISOString(),
      likes: 0
    };
    
    // Add to mock data (in a real app this would be handled by the backend)
    mockComments.unshift(newComment);
    
    return newComment;
  } catch (error) {
    return handleApiError(error);
  }
};

// Mock trending videos data
const mockTrendingVideos: Video[] = [
  {
    id: 'video1',
    videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-woman-walking-under-palm-trees-on-a-tropical-beach-40747-large.mp4',
    thumbnailUrl: 'https://assets.mixkit.co/videos/preview/mixkit-woman-walking-under-palm-trees-on-a-tropical-beach-40747-large.mp4',
    title: 'Paradise Beach Getaway',
    creator: {
      id: 'user1',
      name: 'TravelExplorer',
      avatar: '/images/avatars/avatar1.jpg'
    },
    likes: 1245,
    comments: 89,
    location: 'Bali, Indonesia',
    tags: ['travel', 'beach', 'vacation'],
    isLiked: false,
    createdAt: '2025-05-28T10:30:00Z'
  },
  {
    id: 'video2',
    videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-aerial-view-of-city-traffic-at-night-11-large.mp4',
    thumbnailUrl: 'https://assets.mixkit.co/videos/preview/mixkit-aerial-view-of-city-traffic-at-night-11-large.mp4',
    title: 'City Lights at Night',
    creator: {
      id: 'user2',
      name: 'UrbanShots',
      avatar: '/images/avatars/avatar2.jpg'
    },
    likes: 3782,
    comments: 142,
    location: 'Tokyo, Japan',
    tags: ['city', 'night', 'timelapse'],
    isLiked: true,
    createdAt: '2025-05-29T15:45:00Z'
  },
  {
    id: 'video3',
    videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-hands-holding-a-smart-watch-with-the-stopwatch-running-32808-large.mp4',
    thumbnailUrl: 'https://assets.mixkit.co/videos/preview/mixkit-hands-holding-a-smart-watch-with-the-stopwatch-running-32808-large.mp4',
    title: 'Tech Review: Latest Smartwatch',
    creator: {
      id: 'user3',
      name: 'TechReviewer',
      avatar: '/images/avatars/avatar3.jpg'
    },
    likes: 982,
    comments: 76,
    tags: ['tech', 'gadgets', 'review'],
    isLiked: false,
    createdAt: '2025-05-30T09:15:00Z'
  },
  {
    id: 'video4',
    videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-young-woman-taking-a-bite-of-a-fresh-pizza-she-is-holding-39765-large.mp4',
    thumbnailUrl: 'https://assets.mixkit.co/videos/preview/mixkit-young-woman-taking-a-bite-of-a-fresh-pizza-she-is-holding-39765-large.mp4',
    title: 'Best Pizza in Town',
    creator: {
      id: 'user4',
      name: 'FoodieFinds',
      avatar: '/images/avatars/avatar4.jpg'
    },
    likes: 4521,
    comments: 203,
    location: 'Naples, Italy',
    tags: ['food', 'pizza', 'travel'],
    isLiked: false,
    createdAt: '2025-05-31T12:20:00Z'
  },
  {
    id: 'video5',
    videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-woman-doing-yoga-at-the-beach-4822-large.mp4',
    thumbnailUrl: 'https://assets.mixkit.co/videos/preview/mixkit-woman-doing-yoga-at-the-beach-4822-large.mp4',
    title: 'Morning Yoga Routine',
    creator: {
      id: 'user5',
      name: 'WellnessJourney',
      avatar: '/images/avatars/avatar5.jpg'
    },
    likes: 2876,
    comments: 124,
    location: 'Tulum, Mexico',
    tags: ['yoga', 'wellness', 'beach'],
    isLiked: true,
    createdAt: '2025-06-01T07:30:00Z'
  }
];

// Mock comments data
const mockComments: VideoComment[] = [
  {
    id: 'comment1',
    videoId: 'video1',
    userId: 'user2',
    userName: 'UrbanShots',
    userAvatar: '/images/avatars/avatar2.jpg',
    content: 'This beach looks amazing! What camera did you use to shoot this?',
    createdAt: '2025-05-29T11:30:00Z',
    likes: 24
  },
  {
    id: 'comment2',
    videoId: 'video1',
    userId: 'user3',
    userName: 'TechReviewer',
    userAvatar: '/images/avatars/avatar3.jpg',
    content: 'Adding this to my travel bucket list!',
    createdAt: '2025-05-29T12:45:00Z',
    likes: 18
  },
  {
    id: 'comment3',
    videoId: 'video2',
    userId: 'user1',
    userName: 'TravelExplorer',
    userAvatar: '/images/avatars/avatar1.jpg',
    content: 'Tokyo at night is absolutely breathtaking!',
    createdAt: '2025-05-30T09:20:00Z',
    likes: 42
  },
  {
    id: 'comment4',
    videoId: 'video3',
    userId: 'user4',
    userName: 'FoodieFinds',
    userAvatar: '/images/avatars/avatar4.jpg',
    content: 'Is this the new Series X? How\'s the battery life?',
    createdAt: '2025-05-31T14:10:00Z',
    likes: 7
  },
  {
    id: 'comment5',
    videoId: 'video4',
    userId: 'user5',
    userName: 'WellnessJourney',
    userAvatar: '/images/avatars/avatar5.jpg',
    content: 'That pizza looks delicious! Is it a traditional Neapolitan?',
    createdAt: '2025-06-01T10:15:00Z',
    likes: 31
  }
];
